<template>
  <div class="gk-pdf-viewer">
    <div v-if="toolbar" class="gk-pdf-viewer-toolbar">
      <div class="toolbar--title">
        <slot name="title" />
      </div>
      <div class="toolbar--zoom">
        <GekkoButton
          @click="decreaseZoom()"
          :icon="'fa-solid fa-minus'"
          variant="link"
          size="sm"
          class="zoom-btn"
        />
        <div class="zoom-text-display">
          {{ parseInt(zoomLevels[currentZoomLevel] * 100) }} %
        </div>
        <GekkoButton
          @click="increaseZoom()"
          :icon="'fa-solid fa-plus'"
          variant="link"
          size="sm"
          class="zoom-btn"
        />
      </div>
      <div class="toolbar--actions">
        <slot name="actions" />
      </div>
    </div>
    <div class="gk-pdf-viewer-container" id="gk-pdf-viewer-container">
      <LoadingSpinner v-if="loading" :display-text="$gettext('Generating preview')" />
    </div>
  </div>
</template>

<script>

import { debounce } from '@/utils/'
import LoadingSpinner from '@/components/dashboard/ui/LoadingSpinner'
import GekkoButton from "@/components/GekkoButton"

export default {
  name: 'GkPDFViewer',
  components: {
    LoadingSpinner,
    GekkoButton
  },
  props: {
    itemPdf: {
      type: String,
      default: null
    },
    toolbar: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      pdfjsLib: window.pdfjsLib,
      initialViewportWidth: null,
      pdfDoc: null,
      loading: true,
      container: null,
      safeViewportArea: (2 * 2 * 16),
      maxViewportWidth: 900,
      viewportWidth: null,
      zoomLevels: [0.25, 0.33, 0.50, 0.67, 0.75, 0.80, 0.90, 1.00, 1.10, 1.25, 1.50, 1.75, 2.00, 2.50, 3.00, 4.00, 5.00],
      currentZoomLevel: 7,
      scrollPosition: { left: 0, top: 0 },
      containerSelector: 'gk-pdf-viewer-container'
    }
  },
  watch: {
    itemPdf (newItem) {
      if (newItem) {
        if (this.container) {
          this.container.replaceChildren()
        }
        this.setupData()
      }
    },
    currentZoomLevel () {
      this.renderDocument(true)
    }
  },
  created () {
    window.addEventListener('resize', this.handleResize)
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize)
  },
  mounted () {
    if (this.itemPdf) {
      this.setupData()
    }
  },
  methods: {
    setupData () {
      this.container = document.getElementById(this.containerSelector);

      // Load the PDF
      this.pdfjsLib.getDocument(this.itemPdf).promise.then((pdfDoc_) => {
        this.pdfDoc = pdfDoc_;

        this.renderDocument()

        this.debouncedReRender = debounce(() => {
          this.renderDocument(true)
        }, 100)

        this.loading = false
      }).catch(error => {
        console.error('Error loading PDF: ', error);
      });
    },
    handleResize () {
      if (this.itemPdf) {
        this.debouncedReRender()
      }
    },
    getViewportWidth () {
      return Math.min((this.container.offsetWidth - this.safeViewportArea), this.maxViewportWidth - this.safeViewportArea);
    },
    renderDocument (replace_children = false) {
      this.initialViewportWidth = this.getViewportWidth()

      const containerFragment = new DocumentFragment()

      for (let i = 1; i <= this.pdfDoc.numPages; i++) {
        // Create a container div for each page
        const pageContainer = document.createElement('div')
        pageContainer.id = 'page-container-' + i
        pageContainer.className = 'pdf-page'

        containerFragment.appendChild(pageContainer)
        
        // Create a canvas element for each page
        const canvas = document.createElement('canvas')
        canvas.id = 'pdf-canvas-' + i
        canvas.className = 'pdf-canvas'
        pageContainer.appendChild(canvas)
        
        // Render each page
        this.pdfDoc.getPage(i).then(page => {
          const viewport = page.getViewport({ scale: (this.initialViewportWidth / page.getViewport({ scale: 1 }).width) * this.zoomLevels[this.currentZoomLevel] })

          const context = canvas.getContext('2d')
          canvas.height = viewport.height
          canvas.width = viewport.width
          
          const renderContext = {
            canvasContext: context,
            viewport: viewport
          }
          page.render(renderContext)

          // Create a div for the text layer
          const textLayerDiv = document.createElement('div')
          textLayerDiv.id = 'textLayer-' + i
          textLayerDiv.className = 'textLayer'
          textLayerDiv.style.height = viewport.height + 'px'
          textLayerDiv.style.width = viewport.width + 'px'

          const textElement = document.getElementById('pdf-canvas-' + i)
          if (textElement) {
            textLayerDiv.style.left = textElement.offsetLeft + 'px'

            pageContainer.appendChild(textLayerDiv)

            // Render the text layer
            page.getTextContent().then(textContent => {
              this.pdfjsLib.renderTextLayer({
                textContent: textContent,
                container: textLayerDiv,
                viewport: viewport,
                textDivs: []
              });
            });
          }
          
        });
      }

      if (replace_children) {
        this.container.replaceChildren()
      }

      this.container.append(containerFragment)

      this.$nextTick(() => {
        // this.setScrollPosition(this.containerSelector)
        this.setScrollPercentPosition(this.containerSelector)
      })



    },
    increaseZoom () {
      this.getScrollPercentage(this.containerSelector)

      if (this.currentZoomLevel < (this.zoomLevels.length - 1)) {
        this.currentZoomLevel = this.currentZoomLevel + 1
      }
    },
    decreaseZoom () {
      this.getScrollPercentage(this.containerSelector)

      if (this.currentZoomLevel > 0) {
        this.currentZoomLevel = this.currentZoomLevel - 1
      }
    },
    getScrollPosition () {

    },
    setScrollPosition () {

    },
    getScrollPercentage (element_id) {
      const element = document.getElementById(element_id)
      if (element) {
        const scrollTop = element.scrollTop
        const scrollHeight = element.scrollHeight
        const clientHeight = element.clientHeight
        let scrollTopPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100

        if (isNaN(scrollTopPercentage)) {
          scrollTopPercentage = 0
        } else if (scrollTopPercentage > 100) {
          scrollTopPercentage = 100
        } else if (scrollTopPercentage < 0) {
          scrollTopPercentage = 0
        }

        const scrollLeft = element.scrollLeft
        const scrollWidth = element.scrollWidth
        const clientWidth = element.clientWidth
        let scrollLeftPercentage = (scrollLeft / (scrollWidth - clientWidth)) * 100

        if (isNaN(scrollLeftPercentage)) {
          scrollLeftPercentage = 0
        } else if (scrollLeftPercentage > 100) {
          scrollLeftPercentage = 100
        } else if (scrollLeftPercentage < 0) {
          scrollLeftPercentage = 0
        }

        this.scrollPosition.top = scrollTopPercentage
        this.scrollPosition.left = scrollLeftPercentage
      }
    },
    setScrollPercentPosition (element_id) {
      const element = document.getElementById(element_id)
      if (element) {
        const scrollHeight = element.scrollHeight
        const clientHeight = element.clientHeight
        const scrollTop = (this.scrollPosition.top / 100) * (scrollHeight - clientHeight)
        element.scrollTop = scrollTop
        
        const scrollWidth = element.scrollWidth
        const clientWidth = element.clientWidth
        const scrollLeft = (this.scrollPosition.left / 100) * (scrollWidth - clientWidth)
        element.scrollLeft = scrollLeft
      }
    }
  }
}
</script>

<style lang="scss">
  .gk-pdf-viewer {
    width: 100%;
    display: flex;
    flex-direction: column;

    .gk-pdf-viewer-toolbar {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      height: 56px;
      padding: 0 1rem;
      background: #333639;
      color: white;
      box-shadow: $filtersShadow;
      font-size: 14px;
      z-index: 10;
      .toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        &--title, &--zoom, &--actions {
          flex-grow: 1;
          flex-basis: 0;
        }
        &--title {
          display: none;
          font-family: DINNextLTPro-Medium, sans-serif;
        }
        &--zoom {
          display: flex;
          gap: 0.25rem;
          justify-content: center;
          align-items: center;
          .zoom-btn {
            color: white;
            border-radius: $radiusXl;
            width: 2rem;
            height: 2rem;
            padding: 0;
            border: 0;
            &:hover {
              background: #535353
            }
          }
          .zoom-text-display {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
          }
        }
        &--actions {
          display: flex;
          justify-content: flex-end;
          gap: 0.5rem;
        }
        @media (min-width: 576px) {
          &--title {
            display: block;
          }
          &--zoom {
          }
          &--actions {
            gap: 1rem;
          }

        }
      }
    }

    .gk-pdf-viewer-container {
      position: relative;
      overflow: auto;
      width: 100%;
      height: 100%;
      background: #525659;

      display: flex;
      flex-direction: column;
      // align-items: center;

      .pdf-page { 
        position: relative;
        text-align: center;
        .pdf-canvas {}
        .textLayer {
          // span { color: red !important; }
        }

        /* Copyright 2014 Mozilla Foundation
        *
        * Licensed under the Apache License, Version 2.0 (the "License");
        * you may not use this file except in compliance with the License.
        * You may obtain a copy of the License at
        *
        *     http://www.apache.org/licenses/LICENSE-2.0
        *
        * Unless required by applicable law or agreed to in writing, software
        * distributed under the License is distributed on an "AS IS" BASIS,
        * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
        * See the License for the specific language governing permissions and
        * limitations under the License.
        */

        .textLayer {
          position: absolute;
          text-align: initial;
          inset: 0;
          overflow: clip;
          opacity: 1;
          line-height: 1;
          text-size-adjust: none;
          forced-color-adjust: none;
          transform-origin: 0 0;
          caret-color: CanvasText;
          z-index: 0;

          &.highlighting {
            touch-action: none;
          }

          :is(span, br) {
            color: transparent;
            position: absolute;
            white-space: pre;
            cursor: text;
            transform-origin: 0% 0%;
          }

          > :not(.markedContent),
          .markedContent span:not(.markedContent) {
            z-index: 1;
          }

          /* Only necessary in Google Chrome, see issue 14205, and most unfortunately
          * the problem doesn't show up in "text" reference tests. */
          /*#if !MOZCENTRAL*/
          span.markedContent {
            top: 0;
            height: 0;
          }
          /*#endif*/

          .highlight {
            --highlight-bg-color: rgb(180 0 170 / 0.25);
            --highlight-selected-bg-color: rgb(0 100 0 / 0.25);
            --highlight-backdrop-filter: none;
            --highlight-selected-backdrop-filter: none;

            @media screen and (forced-colors: active) {
              --highlight-bg-color: transparent;
              --highlight-selected-bg-color: transparent;
              --highlight-backdrop-filter: var(--hcm-highlight-filter);
              --highlight-selected-backdrop-filter: var(
                --hcm-highlight-selected-filter
              );
            }

            margin: -1px;
            padding: 1px;
            background-color: var(--highlight-bg-color);
            backdrop-filter: var(--highlight-backdrop-filter);
            border-radius: 4px;

            &.appended {
              position: initial;
            }

            &.begin {
              border-radius: 4px 0 0 4px;
            }

            &.end {
              border-radius: 0 4px 4px 0;
            }

            &.middle {
              border-radius: 0;
            }

            &.selected {
              background-color: var(--highlight-selected-bg-color);
              backdrop-filter: var(--highlight-selected-backdrop-filter);
            }
          }

          ::selection {
            /*#if !MOZCENTRAL*/
            background: rgba(0 0 255 / 0.25);
            /*#endif*/
            /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
            background: color-mix(in srgb, AccentColor, transparent 75%);
          }

          /* Avoids https://github.com/mozilla/pdf.js/issues/13840 in Chrome */
          /*#if !MOZCENTRAL*/
          br::selection {
            background: transparent;
          }
          /*#endif*/

          .endOfContent {
            display: block;
            position: absolute;
            inset: 100% 0 0;
            z-index: 0;
            cursor: default;
            user-select: none;

            &.active {
              top: 0;
            }
          }
        }
      }
    }
  }

</style>